import type { Identifier } from "ra-core";

import type { DealActivityResult } from "../types";

/**
 * Almikia Epic EA (TASK-114): pure, framework-free helpers backing the
 * "Resultados posibles" section of the activity edit screen -- label/message
 * template/next-step-offset validation, next-position computation, list
 * ordering, the rule summary and error-message mapping. Kept free of
 * react-admin/react coupling so they are unit-testable on their own (see
 * `activityResultForm.test.ts`), mirroring the shape of `activityTypeForm.ts`.
 * The exact limits and messages mirror `activityCatalogueProvider.ts`'s
 * FakeRest guards (DATA CONTRACT EA (2)), which mirror the SQL checks.
 */

export const RESULT_LABEL_MAX_LENGTH = 80;
export const MIN_NEXT_STEP_OFFSET_HOURS = 0;
export const MAX_NEXT_STEP_OFFSET_HOURS = 2160;

/**
 * DATA CONTRACT EA (2): a result `label`, trimmed non-blank, at most 80
 * chars, unique case-insensitively among `activeLabelsForActivity` (every
 * OTHER non-archived result of the SAME activity -- the caller excludes the
 * record's own current label before calling this on an edit).
 */
export function validateResultLabel(
  label: string | null | undefined,
  activeLabelsForActivity: readonly string[],
): string | undefined {
  const trimmed = (label ?? "").trim();
  if (trimmed === "") {
    return "resources.deal_activity_results.error.label_required";
  }
  if (trimmed.length > RESULT_LABEL_MAX_LENGTH) {
    return "resources.deal_activity_results.error.label_too_long";
  }
  const normalized = trimmed.toLowerCase();
  const duplicate = activeLabelsForActivity.some(
    (existing) => existing.trim().toLowerCase() === normalized,
  );
  if (duplicate) {
    return "resources.deal_activity_results.error.label_duplicate";
  }
  return undefined;
}

/** DATA CONTRACT EA (2): `message_template`, optional, at most 1000 chars. */
export function validateResultMessageTemplate(
  value: string | null | undefined,
): string | undefined {
  if (value == null || value === "") return undefined;
  if (value.length > 1000) {
    return "resources.deal_activity_results.error.message_template_too_long";
  }
  return undefined;
}

/**
 * DATA CONTRACT EA (2): `next_step_offset_hours` is required exactly when a
 * next activity is chosen, and must be an integer between 0 and 2160 when
 * given -- the CHECK the SQL enforces
 * `(next_activity_type_id IS NULL) = (next_step_offset_hours IS NULL)`.
 */
export function validateNextStepOffsetHours(
  value: number | string | null | undefined,
  hasNextActivity: boolean,
): string | undefined {
  if (!hasNextActivity) return undefined;
  if (value == null || value === "") {
    return "resources.deal_activity_results.error.offset_required";
  }
  const num = typeof value === "string" ? Number(value) : value;
  if (
    !Number.isInteger(num) ||
    num < MIN_NEXT_STEP_OFFSET_HOURS ||
    num > MAX_NEXT_STEP_OFFSET_HOURS
  ) {
    return "resources.deal_activity_results.error.offset_invalid";
  }
  return undefined;
}

/**
 * A new result is added at the end of its activity's own result order (the
 * ticket description): one position step past the highest `position` already
 * used AMONG THAT ACTIVITY'S results (archived ones included, so a freed
 * slot is never reused) -- mirrors `activityTypeForm.ts`'s
 * `nextActivityTypePosition`.
 */
export function nextResultPosition(
  resultsForActivity: readonly { position: number }[],
): number {
  const positions = resultsForActivity.map((result) => result.position);
  return positions.length > 0 ? Math.max(...positions) + 10 : 10;
}

/** Ascending `(position, id)` -- the shared tie-break used across EA screens. */
function comparePositionThenId(
  a: { position: number; id: Identifier },
  b: { position: number; id: Identifier },
): number {
  if (a.position !== b.position) return a.position - b.position;
  return String(a.id).localeCompare(String(b.id), undefined, {
    numeric: true,
  });
}

/**
 * The section's row order (ticket acceptance criterion: "listing the
 * activity's results in order"): active (non-archived) results first, then
 * archived ones, each group by `(position, id)` -- mirrors
 * `activityTypeForm.ts`'s `orderActivityTypesForList`, but unlike
 * `activityLogRules.ts`'s `orderResultsForActivity` this ALSO returns the
 * archived rows (the admin section shows them with a StatusPill instead of
 * hiding them, unlike the log form's picker).
 */
export function orderActivityResultsForList(
  results: readonly DealActivityResult[],
): DealActivityResult[] {
  const active = results
    .filter((result) => result.archived_at == null)
    .slice()
    .sort(comparePositionThenId);
  const archived = results
    .filter((result) => result.archived_at != null)
    .slice()
    .sort(comparePositionThenId);
  return [...active, ...archived];
}

/** The one-line "next step and when" rule summary shown next to each result. */
export interface NextStepRuleSummary {
  hasNextStep: boolean;
  /** `null` when the result has no next step, or when the next activity can't be resolved. */
  nextActivityName: string | null;
  /** `null` when the result has no next step. */
  hours: number | null;
}

/**
 * DATA CONTRACT EA (2): summarizes a result's "next step and when" rule --
 * used to render "Siguiente paso: {activity} en {N} h" or "Sin siguiente
 * paso" (ticket acceptance criterion). Pure data only: the caller translates
 * it into the localized sentence (i18n keys, not baked-in Spanish text here).
 */
export function computeNextStepRuleSummary(
  result: DealActivityResult,
  nextActivityName: string | null | undefined,
): NextStepRuleSummary {
  if (result.next_activity_type_id == null) {
    return { hasNextStep: false, nextActivityName: null, hours: null };
  }
  return {
    hasNextStep: true,
    nextActivityName: nextActivityName ?? null,
    hours: result.next_step_offset_hours ?? null,
  };
}

/**
 * DATA CONTRACT EA (2): maps a save/write error to a Spanish message, so a
 * raw Postgres/RLS error text is never shown to direccion -- mirrors
 * `activityTypeForm.ts`'s `toActivityTypeErrorMessage`. A FakeRest guard
 * error is already a plain Spanish message (see `activityCatalogueProvider.ts`),
 * so it passes through UNCHANGED here -- only a message-less error falls
 * back to the generic message.
 */
export function toActivityResultErrorMessage(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: unknown }).code)
      : undefined;
  const message = error instanceof Error ? error.message : undefined;

  if (code === "23505" || /duplicate key/i.test(message ?? "")) {
    return "resources.deal_activity_results.error.label_duplicate";
  }
  if (code === "42501" || /row-level security/i.test(message ?? "")) {
    return "resources.deal_activity_results.error.not_authorized";
  }
  return message || "resources.deal_activity_results.error.generic";
}
